import React from "react";
import siteConfig from "../config/siteConfig";
import PolicyPage from "./PolicyPage";
import "../css/terms.scss";

function Terms() {
  const sections = [
    {
      id: "about",
      title: "About these terms",
      paragraphs: [
        `These terms govern use of the ${siteConfig.brandName} website and purchases from ${siteConfig.company.legalName}. Our contact address is ${siteConfig.contact.address}, and our customer-care email is ${siteConfig.contact.email}.`,
        "By placing an order, you confirm that you are legally able to enter a contract, that the information you provide is complete and accurate, and that you accept these terms together with the policies linked at checkout. Mandatory consumer rights in your location are not limited by these terms.",
      ],
    },
    {
      id: "site-use",
      title: "Using this website",
      paragraphs: [
        "You may use the site for lawful, personal shopping. You must not interfere with its operation, bypass security, introduce malicious code, use automated access at a volume that burdens the service, impersonate another person, collect other users' data or use the site for fraud or unlawful activity.",
        "We may restrict access where reasonably necessary for security, maintenance or suspected misuse. Availability may occasionally be interrupted, and content or functions may be updated without notice.",
      ],
    },
    {
      id: "products",
      title: "Products and descriptions",
      paragraphs: [
        "We take reasonable care to describe each product accurately, including material, colour, measurements and care information. Screen settings can affect colour, measurements are approximate, and natural fibres may show small variations in shade, texture or dimensions. These characteristics are not faults where they are consistent with the product description and normal qualities of the material.",
        "Images illustrate the product but may not show every detail. The product description, selected options, price and availability shown when you order form the basis of your purchase.",
      ],
    },
    {
      id: "availability",
      title: "Availability and purchase limits",
      paragraphs: [
        "Products are offered subject to availability. Adding an item to a bag does not reserve it. We may apply reasonable quantity limits, reject orders that appear to be for unauthorised resale, or correct an inventory error before accepting an order.",
        "If an ordered item becomes unavailable, we will contact you and refund the affected amount. We will not replace it with a different product without your agreement.",
      ],
    },
    {
      id: "prices",
      title: "Prices, currency and taxes",
      paragraphs: [
        `Prices are displayed in ${siteConfig.currency}. The checkout summary shows the product subtotal, discounts, delivery cost and final amount before payment. Any duties, import taxes or destination charges not collected by us are the customer's responsibility where permitted by law.`,
        "We may change prices prospectively. A price change does not affect an order already accepted. If a price or description contains an obvious error, we may contact you for instructions or cancel the affected item and refund any payment rather than knowingly accept an order at the incorrect price.",
      ],
    },
    {
      id: "orders",
      title: "Order and contract formation",
      paragraphs: [
        "Your checkout submission is an offer to purchase the items in your order. An on-screen message or automated email acknowledging receipt does not by itself mean the order has been accepted. A sales contract is formed when we send a dispatch confirmation, unless we expressly confirm acceptance earlier.",
        "We may decline or cancel an order before acceptance because of unavailable stock, failed payment, suspected fraud, an undeliverable destination, an obvious error, a sanctions or legal restriction, or a breach of these terms. If payment has been captured, the cancelled amount will be refunded to the original payment method.",
      ],
    },
    {
      id: "payment",
      title: "Payment",
      paragraphs: [
        "Payment is completed through the secure page of our payment partner. You authorise the displayed total when you select the final payment action. The provider may perform authentication and fraud checks, and your bank may place a temporary authorisation hold even if an order is later declined.",
        "You must use a payment method you are authorised to use. We are not responsible for fees or exchange rates applied independently by your card issuer or bank. Further details appear in the payment policy.",
      ],
    },
    {
      id: "delivery",
      title: "Delivery and risk",
      paragraphs: [
        "Available methods, charges and estimated delivery times are shown at checkout. Estimates begin after dispatch and are not guaranteed unless expressly stated. Risk in physical goods passes when the parcel is delivered to you or a person you identify, subject to mandatory law. Ownership passes when full payment is received.",
        "You are responsible for providing a complete delivery address and for following reasonable carrier collection instructions. Our shipping policy explains delays, duties, unsuccessful delivery and missing or damaged parcels.",
      ],
    },
    {
      id: "cancellation-returns",
      title: "Cancellation, returns and refunds",
      paragraphs: [
        `You may ask to cancel before dispatch. Once fulfilment has progressed too far to stop the parcel, the request will be handled as a return. Eligible items may be returned within ${siteConfig.shipping.returnsWindowDays} calendar days of delivery under our returns policy.`,
        "Refunds are made to the original payment method unless another method is required by law. Nothing in these terms limits remedies for goods that are faulty, unsafe, misdescribed or otherwise fail to meet non-excludable legal standards.",
      ],
    },
    {
      id: "promotions",
      title: "Promotions and discount codes",
      paragraphs: [
        "A promotion may have additional dates, eligibility rules, product exclusions, territories or spending requirements. Unless stated otherwise, codes cannot be combined, exchanged for cash or applied after an order is placed. We may cancel a discount obtained through manipulation, unauthorised publication, technical error or other misuse while preserving the customer's right to a refund of any resulting cancelled order.",
      ],
    },
    {
      id: "intellectual-property",
      title: "Intellectual property",
      paragraphs: [
        `The ${siteConfig.brandName} name, original text, product information, graphics, photographs, layout and other store content are owned by us or used with permission and are protected by applicable intellectual-property laws. No ownership rights are transferred to you.`,
        "You may view and retain an order record for personal use. You may not copy, republish, sell, modify, remove notices from, create misleading imitations of, or use site content to imply affiliation or endorsement without written permission. Third-party names and software remain the property of their respective owners.",
      ],
    },
    {
      id: "reviews-submissions",
      title: "Messages and submitted content",
      paragraphs: [
        "Information you send must be accurate, lawful and must not infringe another person's rights. You keep ownership of original content you submit, but grant us a non-exclusive right to use it only as reasonably needed to provide customer care, investigate an issue, fulfil your request or comply with law. We will not use customer photographs in public marketing without separate permission.",
      ],
    },
    {
      id: "third-parties",
      title: "Third-party services and links",
      paragraphs: [
        "Payment, delivery and other external services may be governed by their own terms and privacy notices. A link does not mean we control or endorse an external site. We are not responsible for third-party content or acts outside our reasonable control, but this does not remove any responsibility we have for providers acting on our behalf under applicable law.",
      ],
    },
    {
      id: "liability",
      title: "Our responsibility",
      paragraphs: [
        "We are responsible for losses that are a foreseeable result of our breach of contract or failure to use reasonable care. We are not responsible for loss that was not foreseeable, for business loss arising from a consumer purchase, or for delay caused by events outside our reasonable control where we take reasonable steps to reduce the effect.",
        "Nothing in these terms excludes or limits liability where doing so would be unlawful, including liability for fraud, fraudulent misrepresentation, or death or personal injury caused by negligence, or any non-excludable consumer remedy.",
      ],
    },
    {
      id: "events-control",
      title: "Events outside our control",
      paragraphs: [
        "We are not in breach for a delay or failure caused by an event we could not reasonably prevent, such as severe weather, transport interruption, customs action, epidemic, labour disruption, cyber incident, utility failure or government restriction. We will communicate material delays and, where performance becomes impossible or excessively delayed, offer any cancellation or refund required by law.",
      ],
    },
    {
      id: "law",
      title: "Law, disputes and changes",
      paragraphs: [
        "These terms are governed by the laws of the Hong Kong Special Administrative Region. Hong Kong courts have non-exclusive jurisdiction, which means you may also be entitled to bring a claim in another court made available by mandatory consumer law where you live.",
        "If one provision is found invalid, the remaining provisions continue to apply. A delay in enforcing a right is not a waiver. We may update these terms prospectively; the version accepted when an order was placed continues to govern that order unless a change is required by law or agreed with you.",
      ],
    },
    {
      id: "contact",
      title: "Contact",
      paragraphs: [
        `Questions, notices and complaints may be sent to ${siteConfig.contact.email} or by post to ${siteConfig.contact.address}. Please include your order number where relevant so we can locate the transaction.`
      ],
    },
  ];

  return (
    <div className="terms">
      <PolicyPage
        eyebrow="Legal"
        title="Terms and conditions"
        intro={`Terms for browsing and ordering from ${siteConfig.brandName}.`}
        sections={sections}
        relatedLinks={[
          { label: "Payment policy", to: "/payments" },
          { label: "Shipping", to: "/shipping" },
          { label: "Returns and refunds", to: "/returns" },
          { label: "All policies", to: "/policies" },
        ]}
      />
    </div>
  );
}

export default Terms;
